import { Component, effect, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Chip } from 'primeng/chip';

import {
  PackageForIndividualResponse,
  PackagesClient,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { InfoCircle } from '../../../infrastructure/shared/icons/info-circle/info-circle';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { Calendar } from '../../../infrastructure/shared/icons/calendar/calendar';
import {
  getDateFormatedSr,
  getDateFormatedSrWithTime,
} from '../../../infrastructure/shared/services/date.service';

@Component({
  standalone: true,
  selector: 'app-admin-client-individual-package-tab',
  imports: [Chip, InfoCircle, Cube01, Calendar],
  templateUrl: './admin-client-individual-package-tab.html',
  styleUrl: './admin-client-individual-package-tab.scss',
})
export class AdminClientIndividualPackageTab {
  private _client = inject(PackagesClient);

  clientId = input.required<string>();

  pkg = signal<PackageForIndividualResponse | undefined>(undefined);
  isLoaded = signal(false);

  constructor() {
    effect(() => {
      const id = this.clientId();
      if (id) {
        this._load();
      }
    });
  }

  get hasPackage(): boolean {
    const p = this.pkg();
    return !!p && p.status !== undefined;
  }

  get isNew(): boolean {
    return this.pkg()?.status?.value === UserPackageStatus.NEW;
  }

  get isInvoiceSent(): boolean {
    return this.pkg()?.status?.value === UserPackageStatus.INVOICE_SENT;
  }

  get isActive(): boolean {
    return this.pkg()?.status?.value === UserPackageStatus.ACTIVE;
  }

  get title(): string {
    if (this.isActive) return 'Aktivan paket';
    return 'U obradi';
  }

  get subtitleLabel(): string {
    if (this.isNew) return 'Zahtev poslat:';
    if (this.isInvoiceSent) return 'Faktura poslata:';
    if (this.isActive) return 'Važi do:';
    return '';
  }

  get subtitleValue(): string {
    const p = this.pkg();
    if (!p) return '-';
    if (this.isNew) {
      return p.createdOn ? getDateFormatedSrWithTime(p.createdOn) : '-';
    }
    if (this.isInvoiceSent) {
      return p.invoiceSentOn ? getDateFormatedSrWithTime(p.invoiceSentOn) : '-';
    }
    if (this.isActive) {
      return p.validUntil ? getDateFormatedSr(p.validUntil) : '-';
    }
    return '-';
  }

  get chipLabel(): string {
    if (this.isNew) return 'Novi zahtev';
    if (this.isInvoiceSent) return 'Faktura poslata';
    if (this.isActive) return 'Uplata potvrđena';
    return this.pkg()?.status?.description ?? '';
  }

  get chipClass(): string {
    if (this.isNew) return 'info sm';
    if (this.isInvoiceSent) return 'warn sm';
    if (this.isActive) return 'success sm';
    return 'gray sm';
  }

  get datumZahtevaFormatted(): string {
    const d = this.pkg()?.createdOn;
    return d ? getDateFormatedSr(d) : '-';
  }

  private async _load() {
    const res = await firstValueFrom(this._client.getPackagesForIndividual(this.clientId()));
    this.pkg.set(res.result);
    this.isLoaded.set(true);
  }
}
